import { User, ChevronDown, Flame, MessageCircle, PackageCheck } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useOrders } from "@/lib/OrdersContext";
import { COLUMNS, eur, shortId, whatsappUrl } from "@/lib/workshop";

const iconBtn = "flex h-7 w-7 items-center justify-center rounded-md transition-colors duration-200";

const UrgentToggle = ({ order }) => {
  const { toggleUrgente } = useOrders();
  return (
    <button data-testid={`order-urgent-toggle-${order.id}`} onClick={() => toggleUrgente(order.id)}
      title={order.urgente ? "Quitar prioridad" : "Marcar como prioritaria / urgente"} aria-label="Prioritaria / Urgente"
      className={`${iconBtn} ${order.urgente ? "bg-orange-500 text-black hover:bg-orange-400" : "text-zinc-500 hover:bg-white/5 hover:text-orange-400"}`}>
      <Flame className="h-4 w-4" />
    </button>
  );
};

const WhatsAppButton = ({ order }) => {
  const { workshopName } = useOrders();
  const url = whatsappUrl(order, workshopName);
  if (!url) {
    return <span title="Sin teléfono" className={`${iconBtn} cursor-not-allowed text-zinc-700`}><MessageCircle className="h-4 w-4" /></span>;
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" data-testid={`order-whatsapp-btn-${order.id}`} draggable={false}
      title="Enviar estado por WhatsApp" aria-label="Enviar estado por WhatsApp"
      className={`${iconBtn} text-emerald-400 hover:bg-emerald-500/15`}>
      <MessageCircle className="h-4 w-4" />
    </a>
  );
};

const PaymentBadge = ({ order }) => {
  const { togglePago } = useOrders();
  return (
    <button
      data-testid={`order-payment-toggle-${order.id}`}
      onClick={() => togglePago(order.id)}
      title="Cambiar estado de pago"
      className={`rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors duration-200 ${order.pagado ? "bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25" : "bg-[var(--gold-soft)] text-[var(--gold)] hover:bg-[var(--gold)]/25"}`}
    >
      {order.pagado ? "Pagado" : "Pendiente"}
    </button>
  );
};

const StatusMenu = ({ order }) => {
  const { moveOrder } = useOrders();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid={`order-status-menu-${order.id}`} className="flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] text-zinc-500 transition-colors hover:bg-white/5 hover:text-zinc-200">
        Mover <ChevronDown className="h-3 w-3" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="border-white/10 bg-[#111114]">
        {COLUMNS.filter((c) => c.id !== order.estado).map((c) => (
          <DropdownMenuItem key={c.id} data-testid={`order-move-${order.id}-${c.id}`} onClick={() => moveOrder(order.id, c.id)}>
            <span className="mr-2 h-2 w-2 rounded-full" style={{ background: c.accent }} />{c.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const OrderCard = ({ order }) => (
  <div
    data-testid={`order-card-${order.id}`}
    draggable
    onDragStart={(e) => { e.dataTransfer.setData("text/plain", order.id); e.dataTransfer.effectAllowed = "move"; }}
    className={`rise group cursor-grab rounded-xl border bg-[#141418] p-4 transition-[border-color,transform] duration-200 hover:-translate-y-0.5 active:cursor-grabbing ${order.urgente ? "border-orange-500/70 shadow-[0_0_0_1px_rgba(249,115,22,0.25)]" : "border-white/5 hover:border-[var(--gold)]/40"}`}
  >
    <div className="flex items-start justify-between gap-2">
      <div className="flex flex-wrap items-center gap-1.5">
        <span data-testid={`order-plate-${order.id}`} className="font-mono-plate rounded border border-white/15 bg-black/40 px-2 py-0.5 text-xs font-bold tracking-wider text-zinc-100">
          {order.matricula || "—"}
        </span>
        {order.urgente && (
          <span data-testid={`order-urgent-tag-${order.id}`} className="rounded bg-orange-500 px-1.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-black">Urgente</span>
        )}
      </div>
      <div className="flex items-center gap-0.5">
        <WhatsAppButton order={order} />
        <UrgentToggle order={order} />
      </div>
    </div>
    <p className="mt-3 text-sm font-semibold text-zinc-100">{order.modelo || "Vehículo"}</p>
    <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-zinc-400">{order.servicio || "Sin descripción"}</p>
    <div className="mt-3 flex items-center justify-between gap-1.5 text-xs text-zinc-500">
      <span className="flex min-w-0 items-center gap-1.5"><User className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{order.cliente || "Sin cliente"}</span></span>
      <span className="shrink-0 text-[10px] text-zinc-600">#{shortId(order.id)}</span>
    </div>
    <div className="mt-4 flex items-center justify-between border-t border-white/5 pt-3">
      <span data-testid={`order-amount-${order.id}`} className="font-display text-base font-bold text-[var(--gold)]">{eur(order.importe)}</span>
      <div className="flex items-center gap-1">
        <StatusMenu order={order} />
        <PaymentBadge order={order} />
      </div>
    </div>
    {order.estado === "listo_entregar" && <DeliverButton order={order} />}
  </div>
);

const DeliverButton = ({ order }) => {
  const { markDelivered } = useOrders();
  return (
    <button data-testid={`order-deliver-btn-${order.id}`} onClick={() => markDelivered(order.id)}
      className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg bg-[var(--gold)] py-2 text-sm font-bold text-black transition-colors duration-200 hover:bg-amber-400">
      <PackageCheck className="h-4 w-4" /> Marcar como Entregado
    </button>
  );
};
